import React, { useRef, useState } from "react";

import { ProductEntryDTO } from "@energy-advice/easas-api";
import { ITableDataEntry, MongoFile } from "components/dashboard/product-template-table/utils/interfaces";
import { errorToaster, successToaster } from "components/pages/measurement-devices/utils/utils";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload, FileUploadUploadEvent } from "primereact/fileupload";
import { EASAS_REST_URL } from "utils/constants";
import { FileType } from "utils/enums";
import { T } from "services/lang/LanguageService";

type EntryFileProps = {
  entryData: ITableDataEntry;
  value: MongoFile;
  cellId: string;
  changeHandler?: (receivedValue: MongoFile | null) => void;
  isInEditMode: boolean;
  showThumbnail?: boolean;
  fileType?: FileType;
};

const EntryFile: React.FC<EntryFileProps> = ({
  entryData,
  value,
  cellId,
  changeHandler,
  isInEditMode,
  showThumbnail,
  fileType,
}) => {
  const [dialog, setDialog] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(true);

  const file = value?.files?.[0];

  const getFileUrl = (type: "download" | "upload") => {
    let url = `${EASAS_REST_URL()}/productTemplates/${type}/${entryData.template}/${entryData.id}/${cellId}`;
    if (type === "download") {
      url += `/${file?.key}`;
    }
    return encodeURI(url);
  };

  return (
    <>
      {showThumbnail && fileType === FileType.IMAGE && file?.key ? (
        <div onClick={() => setDialog(true)} className="relative flex flex-column align-items-center">
          {isLoadingFile && <i className="absolute top-50 pi pi-spin pi-spinner" />}
          <img
            src={getFileUrl("download")}
            alt=""
            width={showThumbnail ? 50 : 0}
            height={showThumbnail ? 50 : 0}
            onLoad={() => setIsLoadingFile(false)}
            onError={() => setIsLoadingFile(false)}
            loading="lazy"
          />
        </div>
      ) : (
        <Button icon="pi pi-folder" onClick={() => setDialog(true)} text />
      )}
      <Dialog
        visible={dialog}
        header={file?.fileName}
        onHide={() => setDialog(false)}
        resizable
        style={{ width: "min(100%, 50rem)" }}
      >
        <EntryFileDisplay
          cellId={cellId}
          changeHandler={changeHandler}
          isInEditMode={isInEditMode}
          value={value}
          fileType={fileType}
          getFileUrl={getFileUrl}
        />
      </Dialog>
    </>
  );
};

export default EntryFile;

type EntryFileDisplayProps = {
  getFileUrl: (type: "download" | "upload") => string;
} & Pick<EntryFileProps, "cellId" | "changeHandler" | "isInEditMode" | "value" | "fileType">;

const EntryFileDisplay: React.FC<EntryFileDisplayProps> = ({
  cellId,
  changeHandler,
  isInEditMode,
  value,
  fileType,
  getFileUrl,
}) => {
  const [isLoadingFile, setIsLoadingFile] = useState(true);
  const uploadRef = useRef<FileUpload>(null);
  const file = value?.files?.[0];

  const onUpload = (e: FileUploadUploadEvent) => {
    const response: ProductEntryDTO = JSON.parse(e.xhr.response);
    const responseValue = response.entries?.[cellId].value;
    if (responseValue) {
      changeHandler?.(responseValue);
      successToaster(T("alert.file-uploaded"));
    }
  };

  const getAcceptFile = () => {
    switch (fileType) {
      case FileType.IMAGE:
        return "image/*";
      case FileType.AUDIO:
        return "audio/*";
      case FileType.VIDEO:
        return "video/*";
      default:
        return "*/*";
    }
  };

  const handleIframeLoad = () => {
    setIsLoadingFile(false);
  };

  return (
    <div className="flex flex-column align-items-center">
      {isInEditMode && (
        <FileUpload
          ref={uploadRef}
          name="file"
          mode="advanced"
          url={getFileUrl("upload")}
          className="w-full"
          auto
          chooseOptions={{
            icon: "pi pi-upload",
          }}
          onUpload={onUpload}
          onError={e => {
            const resp = JSON.parse(e.xhr.response);
            errorToaster(resp.message);
            uploadRef.current?.clear;
          }}
          accept={getAcceptFile()}
        />
      )}
      {!isInEditMode && file?.key && (
        <>
          <Button
            type="button"
            onClick={() => window.open(`${getFileUrl("download")}?dl=true`)}
            icon="pi pi-download"
            label={T("action.download")}
            className="component-editor-button icon-green align-self-end"
          />
          {isLoadingFile && fileType !== FileType.OTHER && <i className="pi pi-spin pi-spinner mb-5" />}
          {fileType === FileType.AUDIO && (
            <audio
              controls
              onLoadedData={() => setIsLoadingFile(false)}
              onError={() => setIsLoadingFile(false)}
            >
              <source src={getFileUrl("download")} />
              <track kind="captions" />
            </audio>
          )}
          {fileType === FileType.VIDEO && (
            <video
              controls
              onLoadedData={() => setIsLoadingFile(false)}
              onError={() => setIsLoadingFile(false)}
              style={{ width: "100%", height: "auto" }}
            >
              <source src={getFileUrl("download")} />
              <track kind="captions" />
            </video>
          )}
          {fileType === FileType.IMAGE && (
            <img
              src={getFileUrl("download")}
              alt=""
              style={{ width: "100%" }}
              onLoad={() => setIsLoadingFile(false)}
              onError={() => setIsLoadingFile(false)}
            />
          )}
          {fileType === FileType.TEXT && (
            <iframe
              loading="lazy"
              src={getFileUrl("download")}
              title="file-view"
              onLoad={handleIframeLoad}
              style={{ maxHeight: "200vh", minHeight: "70vh" }}
              className="flex-1 border-none w-full"
            />
          )}
        </>
      )}
      {!isInEditMode && !file?.key && <div className="text-center">No file uploaded</div>}
    </div>
  );
};
